/**
 * GalleryShelves — the front page's four Netflix-style discovery sections
 * (🏅 Editors' Pick / 💖 Most Liked / ✨ Most Recent / 🏆 Top Rated): Editors'
 * Pick is a single four-slot row, while the ranked shelves use a two-row
 * column-flow grid. Each sits inside a horizontal scroller with ‹ › chevron
 * paging and whole-card scroll snapping.
 *
 * Pure presentation: every shelf is a thin {@link buildGallery} slice (the
 * shelf selectors in gallery/model.ts) derived from the SAME already-
 * subscribed creation items the main grid renders — no shelf-local ranking,
 * no extra subscription. Shelf cards reuse {@link GameCard} shrunk purely via
 * CSS scoping and live OUTSIDE the `gallery-grid` testid — the e2e
 * search-count and style-chip checks count cards inside the grid only.
 */
import { useMemo, useRef } from 'react';
import {
  topRatedShelfItems,
  mostLikedShelfItems,
  mostRecentShelfItems,
  type GalleryItem,
} from '../gallery/model.js';
import { GameCard } from './GameCard.js';

function Shelf({
  id,
  title,
  accent,
  items,
  editHrefFor,
}: {
  id: 'editors-pick' | 'most-liked' | 'most-recent' | 'top-rated';
  title: string;
  accent: 'butter' | 'gum' | 'teal' | 'paper';
  items: GalleryItem[];
  editHrefFor?: (item: GalleryItem) => string | undefined;
}) {
  const scroller = useRef<HTMLDivElement>(null);
  // Page by one viewport of the scroller — whole columns per press; the
  // mandatory scroll snap settles any remainder onto a card edge.
  const page = (dir: -1 | 1) =>
    scroller.current?.scrollBy({ left: dir * scroller.current.clientWidth, behavior: 'smooth' });
  if (items.length === 0) return null;
  return (
    <section
      className={`gallery-shelf${id === 'editors-pick' ? ' gallery-shelf--single-row' : ''}`}
      data-testid={`shelf-${id}`}
    >
      <div className="gallery-shelf__head">
        <h2 className={`shelftitle shelftitle--${accent}`}>{title}</h2>
        <div className="gallery-shelf__nav">
          <button
            type="button"
            className="gallery-shelf__btn"
            aria-label={`${title}: scroll back`}
            onClick={() => page(-1)}
          >
            ‹
          </button>
          <button
            type="button"
            className="gallery-shelf__btn"
            aria-label={`${title}: scroll forward`}
            onClick={() => page(1)}
          >
            ›
          </button>
        </div>
      </div>
      <div className="gallery-shelf__scroller" ref={scroller}>
        {items.map((item) => (
          <GameCard key={`${id}:${item.slug}`} item={item} editHref={editHrefFor?.(item)} />
        ))}
      </div>
    </section>
  );
}

export function GalleryShelves({
  creations,
  editorsPicks,
  editHrefFor,
}: {
  creations: GalleryItem[];
  editorsPicks: GalleryItem[];
  editHrefFor?: (item: GalleryItem) => string | undefined;
}) {
  const topRated = useMemo(() => topRatedShelfItems(creations), [creations]);
  const mostLiked = useMemo(() => mostLikedShelfItems(creations), [creations]);
  const mostRecent = useMemo(() => mostRecentShelfItems(creations), [creations]);
  if (editorsPicks.length + topRated.length + mostLiked.length + mostRecent.length === 0) return null;
  return (
    <div className="gallery-shelves" data-testid="gallery-shelves">
      <Shelf
        id="editors-pick"
        title="🏅 Editors' Pick"
        accent="butter"
        items={editorsPicks}
        editHrefFor={editHrefFor}
      />
      <Shelf id="most-liked" title="💖 Most Liked" accent="gum" items={mostLiked} editHrefFor={editHrefFor} />
      <Shelf id="most-recent" title="✨ Most Recent" accent="teal" items={mostRecent} editHrefFor={editHrefFor} />
      <Shelf id="top-rated" title="🏆 Top Rated" accent="paper" items={topRated} editHrefFor={editHrefFor} />
    </div>
  );
}
